import { paymentsControllerVerifyCallback } from '@kalba/api-client';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';

export const Route = createFileRoute('/payment-callback')({
  validateSearch: (search: Record<string, unknown>) => ({
    // Tahseel echoes back our InternalLogId as the permit ID
    InternalLogId: String(search.InternalLogId ?? search.permitId ?? ''),
    InternalRecordId: search.InternalRecordId ? String(search.InternalRecordId) : undefined,
    TP_ReceiptNo: String(search.TP_ReceiptNo ?? ''),
    TP_RefNo: String(search.TP_RefNo ?? ''),
    TP_SecHash: String(search.TP_SecHash ?? ''),
    TP_Amount: String(search.TP_Amount ?? ''),
    TP_ResultCode: search.TP_ResultCode ? String(search.TP_ResultCode) : undefined,
    TP_Merchant: search.TP_Merchant ? String(search.TP_Merchant) : undefined,
    TP_ServiceInfo: search.TP_ServiceInfo ? String(search.TP_ServiceInfo) : undefined,
    TP_ErrorMessage: search.TP_ErrorMessage ? String(search.TP_ErrorMessage) : undefined,
    TP_PayMethod: search.TP_PayMethod ? String(search.TP_PayMethod) : undefined,
    TP_PaymentDate: search.TP_PaymentDate ? String(search.TP_PaymentDate) : undefined,
    TP_ExtraFees: search.TP_ExtraFees ? String(search.TP_ExtraFees) : undefined,
    TP_TaxFees: search.TP_TaxFees ? String(search.TP_TaxFees) : undefined,
    TP_Corp_NAME_A: search.TP_Corp_NAME_A ? String(search.TP_Corp_NAME_A) : undefined,
    TP_Corp_NAME_E: search.TP_Corp_NAME_E ? String(search.TP_Corp_NAME_E) : undefined,
    TP_Corp_TRN: search.TP_Corp_TRN ? String(search.TP_Corp_TRN) : undefined,
  }),
  component: PaymentCallbackPage,
});

type VerifyState = 'verifying' | 'success' | 'failed' | 'error';

function PaymentCallbackPage() {
  const search = Route.useSearch();
  const [state, setState] = useState<VerifyState>('verifying');
  // Use InternalLogId (echoed permitId) or fall back to TP_RefNo lookup
  const permitId = search.InternalLogId;
  const [resolvedPermitId, setResolvedPermitId] = useState<string>(permitId);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    if (!search.TP_RefNo) {
      setState('error');
      setErrorMessage('Invalid callback — missing required payment parameters.');
      return;
    }

    paymentsControllerVerifyCallback({
      permitId: permitId || search.TP_RefNo,
      requestBody: {
        TP_ReceiptNo: search.TP_ReceiptNo,
        TP_RefNo: search.TP_RefNo,
        TP_SecHash: search.TP_SecHash,
        TP_Amount: search.TP_Amount,
        TP_ResultCode: search.TP_ResultCode,
        TP_Merchant: search.TP_Merchant,
        TP_ServiceInfo: search.TP_ServiceInfo,
        TP_ErrorMessage: search.TP_ErrorMessage,
        TP_PayMethod: search.TP_PayMethod,
        TP_PaymentDate: search.TP_PaymentDate,
        TP_ExtraFees: search.TP_ExtraFees,
        TP_TaxFees: search.TP_TaxFees,
        TP_Corp_NAME_A: search.TP_Corp_NAME_A,
        TP_Corp_NAME_E: search.TP_Corp_NAME_E,
        TP_Corp_TRN: search.TP_Corp_TRN,
        InternalLogId: search.InternalLogId,
        InternalRecordId: search.InternalRecordId,
      },
    })
      .then((result: unknown) => {
        const res = result as { success: boolean; permitId: string };
        if (res.permitId) setResolvedPermitId(res.permitId);
        if (res.success) {
          setState('success');
        } else {
          setState('failed');
          setErrorMessage(search.TP_ErrorMessage ?? 'Payment was not completed.');
        }
      })
      .catch(() => {
        setState('error');
        setErrorMessage('Unable to confirm your payment. Please contact support.');
      });
  }, [search, permitId]);

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-brand-600 px-6 py-4">
          <p className="text-white font-bold text-lg">Istisharat Permit Portal</p>
        </div>

        <div className="p-8 text-center space-y-4">
          {state === 'verifying' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-brand-500 mx-auto" />
              <h2 className="text-lg font-semibold text-gray-800">Confirming your payment…</h2>
              <p className="text-sm text-gray-500">Please wait while we verify your transaction.</p>
            </>
          )}

          {state === 'success' && (
            <>
              <CheckCircle2 className="h-12 w-12 text-brand-500 mx-auto" />
              <h2 className="text-xl font-bold text-gray-900">Payment Confirmed!</h2>
              <p className="text-sm text-gray-600">
                Your payment has been received. Your permit application is being processed.
              </p>
              {resolvedPermitId && (
                <Link
                  to="/permits/$permitId"
                  params={{ permitId: resolvedPermitId }}
                  className="inline-block mt-2"
                >
                  <Button variant="primary">View Permit</Button>
                </Link>
              )}
            </>
          )}

          {(state === 'failed' || state === 'error') && (
            <>
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
              <h2 className="text-xl font-bold text-gray-900">Payment Not Completed</h2>
              <p className="text-sm text-gray-600">
                {errorMessage || 'Your payment was not completed.'}
              </p>
              <div className="flex flex-col gap-2 mt-2">
                {resolvedPermitId && (
                  <Link to="/permits/$permitId/payment" params={{ permitId: resolvedPermitId }}>
                    <Button variant="primary" className="w-full">
                      Try Again
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full">
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
